import React, { useMemo, useCallback, useEffect } from "react";
import Select from "react-select";
import {
  formatUrdPaymentDisplayLabel,
  isUrdLinkedPayMode,
} from "../utils/paymentLinesErpAdapter";
import { formatRoundOffLabel } from "../utils/autoRoundOff";

const AVAILABLE_PAYMENT_METHODS = [
  "cash",
  "cheque",
  "bank_transfer",
  "upi",
  "card",
  "old_gold",
  "old_silver",
  "kasar",
  "scheme",
  "order",
  "os",
  "wallet",
];

const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  upi: "UPI",
  card: "Card",
  old_gold: "Old Gold",
  old_silver: "Old Silver",
  kasar: "Kasar",
  scheme: "Scheme",
  order: "Order",
  os: "OS",
  wallet: "Wallet",
};

/** Payment-in rows (reference layout order). */
const PAYMENT_IN_ROW_CONFIG = [
  { pay_mode: "cash", label: "Cash" },
  { pay_mode: "upi", label: "UPI" },
  { pay_mode: "bank_transfer", label: "Bank Transfer" },
  { pay_mode: "old_gold", label: "Old Gold" },
  { pay_mode: "old_silver", label: "Old Silver" },
  { pay_mode: "cheque", label: "Cheque" },
  { pay_mode: "card", label: "Card" },
  { pay_mode: "kasar", label: "Kasar" },
];

/** Cash paid out to customer (always visible). */
const CASH_PAID_ROW_CONFIG = {
  pay_mode: "cash",
  label: "Cash Paid",
  isPayable: true,
};

const PAYMENT_MODE_COL_WIDTH = "140px";
const PAYMENT_AMT_COL_WIDTH = "110px";
const PAYMENT_SUMMARY_LABEL_COL_WIDTH = "92px";
const PAYMENT_SUMMARY_MIDDLE_COL_WIDTH = "102px";
const PAYMENT_SUMMARY_AMT_LABEL_COL_WIDTH = "100px";
const PAYMENT_SUMMARY_AMT_COL_WIDTH = "132px";
const PAYMENT_TABLE_TEXT_STYLE = { fontSize: "0.85rem" };
const PAYMENT_ROW_HEIGHT = "32px";
const PAYMENT_CELL_INPUT_STYLE = {
  ...PAYMENT_TABLE_TEXT_STYLE,
  height: "100%",
  width: "100%",
  borderRadius: 0,
  boxShadow: "none",
};
const PAYMENT_CELL_INPUT_CLASS =
  "form-control form-control-sm border-0 rounded-0";
const PAYMENT_CELL_READONLY_CLASS =
  "form-control form-control-sm bg-light border-0 rounded-0";
/** Same tint as invoice list `.status-pending-row` */
const PAYMENT_PENDING_ROW_BG = "rgba(220, 53, 69, 0.08)";
const PAYMENT_MODES_HEADER_BG =
  "rgba(var(--bs-primary-rgb, 150, 42, 41), 0.07)";
const PAYMENT_MODES_LABEL_BG =
  "rgba(var(--bs-primary-rgb, 150, 42, 41), 0.035)";
const PAYMENT_SUMMARY_ROW_CSS = `
  .payment-summary-pending-cell {
    background-color: ${PAYMENT_PENDING_ROW_BG} !important;
  }
  .payment-summary-pending-cell.payment-pending-label,
  .payment-summary-pending-cell .payment-pending-label,
  .payment-summary-pending-cell .payment-pending-value {
    color: var(--bs-danger, #dc3545) !important;
  }
  .payment-summary-pending-cell .form-control {
    background-color: transparent !important;
    box-shadow: none !important;
  }
  .payment-summary-success-cell {
    background-color: var(--bs-success-bg-subtle, #ebfaf0) !important;
  }
  .payment-summary-success-cell.payment-success-label,
  .payment-summary-success-cell .payment-success-value {
    color: rgba(var(--bs-success-rgb, 25, 135, 84), 1) !important;
  }
  .payment-summary-success-cell .form-control {
    background-color: transparent !important;
    box-shadow: none !important;
  }
  .payment-modes-header-cell {
    background-color: ${PAYMENT_MODES_HEADER_BG} !important;
    box-shadow: none !important;
    --bs-table-bg: ${PAYMENT_MODES_HEADER_BG};
    --bs-table-accent-bg: transparent;
  }
  .payment-modes-label-cell {
    background-color: ${PAYMENT_MODES_LABEL_BG} !important;
  }
  .payment-section-root input::placeholder,
  .payment-section-root textarea::placeholder {
    color: #adb5bd !important;
    opacity: 1;
  }
  .payment-section-root .payment-final-amount-line {
    color: var(--bs-emphasis-color, #212529) !important;
    font-weight: 400;
  }
  .payment-section-root .payment-final-amount-label {
    font-weight: 600;
  }
  .payment-section-root .payment-final-amount-words {
    font-style: italic;
    font-weight: 400;
  }
  /* Portaled menu — ensure keyboard focus highlight is always visible */
  .payment-type-select__option--is-focused {
    background-color: #deebff !important;
    color: #212529 !important;
  }
  .payment-type-select__option--is-selected {
    background-color: var(--theme-primary, #962a29) !important;
    color: #ffffff !important;
  }
  .payment-type-select__option--is-selected.payment-type-select__option--is-focused {
    background-color: var(--theme-primary, #7a2221) !important;
    color: #ffffff !important;
  }
`;
const PAYMENT_CELL_PENDING_VALUE_CLASS =
  "form-control form-control-sm border-0 rounded-0 payment-pending-value";
const PAYMENT_CELL_SUCCESS_VALUE_CLASS =
  "form-control form-control-sm border-0 rounded-0 payment-success-value";
const PAYMENT_CELL_DANGER_INPUT_STYLE = {
  ...PAYMENT_CELL_INPUT_STYLE,
  backgroundColor: "transparent",
};
const PAYMENT_CELL_SUCCESS_INPUT_STYLE = {
  ...PAYMENT_CELL_INPUT_STYLE,
  backgroundColor: "transparent",
};
const PAYMENT_SUMMARY_PENDING_LABEL_CLASS =
  "p-0 align-middle fw-semibold ps-2 payment-pending-label";
const PAYMENT_SUMMARY_PENDING_VALUE_CELL_CLASS = "p-0 align-middle";
const PAYMENT_SUMMARY_SUCCESS_LABEL_CLASS =
  "p-0 align-middle fw-semibold ps-2 payment-success-label payment-summary-success-cell";
const PAYMENT_SUMMARY_SUCCESS_VALUE_CELL_CLASS =
  "p-0 align-middle payment-summary-success-cell";

const SUMMARY_NOTE_LABEL_STYLE = {
  fontSize: "0.85rem",
  marginBottom: "0.35rem",
  fontWeight: 600,
};

const SUMMARY_NOTE_TEXTAREA_STYLE = {
  resize: "vertical",
  minHeight: "56px",
  paddingRight: "45px",
};

const SUMMARY_INPUT_WRAPPER_STYLE = {
  width: "100%",
};

function numberToWords(num) {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const teens = [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const value = Math.floor(Math.abs(num));
  if (value === 0) return "Zero";

  const convertLessThanThousand = (n) => {
    if (n === 0) return "";
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      return (
        tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ` ${ones[n % 10]}` : "")
      );
    }
    return (
      `${ones[Math.floor(n / 100)]} Hundred` +
      (n % 100 !== 0 ? ` and ${convertLessThanThousand(n % 100)}` : "")
    );
  };

  const convert = (n) => {
    if (n === 0) return "";
    if (n < 1000) return convertLessThanThousand(n);
    if (n < 100000) {
      return (
        `${convertLessThanThousand(Math.floor(n / 1000))} Thousand` +
        (n % 1000 !== 0 ? ` ${convertLessThanThousand(n % 1000)}` : "")
      );
    }
    if (n < 10000000) {
      return (
        `${convertLessThanThousand(Math.floor(n / 100000))} Lakh` +
        (n % 100000 !== 0 ? ` ${convert(n % 100000)}` : "")
      );
    }
    return (
      `${convertLessThanThousand(Math.floor(n / 10000000))} Crore` +
      (n % 10000000 !== 0 ? ` ${convert(n % 10000000)}` : "")
    );
  };

  return convert(value);
}

function formatAmountInWords(amount) {
  const numericAmount =
    parseFloat(String(amount ?? "").replace(/[^\d.-]/g, "")) || 0;
  const rupees = Math.floor(Math.abs(numericAmount));
  const paise = Math.round((Math.abs(numericAmount) - rupees) * 100);

  if (rupees === 0 && paise === 0) {
    return "Rupees Zero Only";
  }

  let words = numberToWords(rupees);
  if (paise > 0) {
    words += ` and ${numberToWords(paise)} Paise`;
  }

  return `Rupees ${words} Only`;
}

function formatAmountInWordsParenthetical(amount) {
  return formatAmountInWords(amount).replace(/^Rupees\s+/i, "");
}

const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "100%",
    height: "100%",
    width: "100%",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:hover": {
      border: "none",
      cursor: "pointer",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "100%",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#000",
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: "#000",
    fontSize: "0.85rem",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "100%",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
    color: "#6c757d",
    display: "none",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: "0.85rem",
    width: "100%",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  // Keyboard/hover highlight must be obvious (native-select style).
  option: (base, state) => {
    const focused = Boolean(state.isFocused);
    const selected = Boolean(state.isSelected);

    let backgroundColor = "#ffffff";
    let color = "#212529";

    if (selected && focused) {
      backgroundColor = "var(--theme-primary, #962a29)";
      color = "#ffffff";
    } else if (selected) {
      backgroundColor = "var(--theme-primary, #962a29)";
      color = "#ffffff";
    } else if (focused) {
      backgroundColor = "#DEEBFF";
      color = "#212529";
    }

    return {
      ...base,
      backgroundColor,
      color,
      cursor: "pointer",
      ":active": {
        ...base[":active"],
        backgroundColor: selected
          ? "var(--theme-primary, #962a29)"
          : "#B2D4FF",
        color: selected ? "#ffffff" : "#212529",
      },
    };
  },
  placeholder: (base) => ({
    ...base,
    color: "#adb5bd",
    fontSize: "0.85rem",
  }),
  container: (base) => ({
    ...base,
    width: "100%",
    height: "100%",
    padding: 0,
    margin: 0,
  }),
};

const PAYMENT_MODES_WITHOUT_CASH_BANK = new Set([
  "cheque",
  "bank_transfer",
  "upi",
  "card",
]);

function isCashBankAccount(account) {
  if (!account) return false;
  const accountType = String(account.accountType || "")
    .toLowerCase()
    .trim();
  const bankName = String(account.bankName || "")
    .toLowerCase()
    .trim();
  return accountType === "cash" || bankName === "cash";
}

function getFixedLedgerTypeOption(
  paymentTypes,
  bankAccounts,
  keyword,
  fallbackLabel,
) {
  const account = findBankAccount(bankAccounts, keyword);
  if (account) {
    const fromTypes = paymentTypes.find(
      (option) => String(option.value) === String(account.id),
    );
    if (fromTypes) return fromTypes;
    return {
      value: String(account.id),
      label: account.bankName || fallbackLabel,
    };
  }

  const fromTypes = paymentTypes.find((option) => {
    const label = String(option.label || "")
      .toLowerCase()
      .trim();
    return label === keyword || label.includes(keyword);
  });
  return fromTypes || { value: keyword, label: fallbackLabel };
}

function getCashTypeOption(paymentTypes, bankAccounts) {
  return getFixedLedgerTypeOption(paymentTypes, bankAccounts, "cash", "Cash");
}

function getTypeOptionsForPayMode(
  payMode,
  paymentTypes,
  bankAccounts,
  isPayable = false,
  row = null,
) {
  const cashBankIds = new Set(
    bankAccounts.filter(isCashBankAccount).map((account) => String(account.id)),
  );

  // Cash is always the fixed cash ledger — never a bank picker.
  if (payMode === "cash") {
    return [getCashTypeOption(paymentTypes, bankAccounts)];
  }

  let opts =
    PAYMENT_MODES_WITHOUT_CASH_BANK.has(payMode) || isPayable
      ? paymentTypes.filter((option) => !cashBankIds.has(String(option.value)))
      : [...paymentTypes];

  // Keep the saved bank visible even if missing from listBankAccounts /
  // filtered as cash (edit invoice must still show INDUSLND etc.).
  const typeStr =
    row?.type != null && String(row.type).trim() !== ""
      ? String(row.type).trim()
      : "";
  const bankDisplayName = String(
    row?.bankDisplayName ?? row?.bank_name ?? "",
  ).trim();

  if (typeStr && !opts.some((opt) => String(opt.value) === typeStr)) {
    opts = [
      {
        value: typeStr,
        label: bankDisplayName || typeStr,
      },
      ...opts,
    ];
  } else if (
    !typeStr &&
    bankDisplayName &&
    !opts.some(
      (opt) =>
        String(opt.label).trim().toLowerCase() ===
        bankDisplayName.toLowerCase(),
    )
  ) {
    opts = [
      {
        value: `__acctname__:${bankDisplayName}`,
        label: bankDisplayName,
      },
      ...opts,
    ];
  }

  return opts;
}

function getSelectedTypeOption(row, typeOptions) {
  const typeStr =
    row?.type != null && String(row.type).trim() !== ""
      ? String(row.type).trim()
      : "";
  if (typeStr) {
    const byValue = typeOptions.find((opt) => String(opt.value) === typeStr);
    if (byValue) return byValue;
  }

  const bankDisplayName = String(
    row?.bankDisplayName ?? row?.bank_name ?? "",
  ).trim();
  if (bankDisplayName) {
    const byLabel = typeOptions.find(
      (opt) =>
        String(opt.label).trim().toLowerCase() ===
        bankDisplayName.toLowerCase(),
    );
    if (byLabel) return byLabel;
    if (typeStr) {
      return { value: typeStr, label: bankDisplayName };
    }
  }

  return null;
}

function amountInputDisplayValue(amount) {
  if (amount === null || amount === undefined || amount === "") return "";
  const numericAmount = parseFloat(amount);
  if (!Number.isFinite(numericAmount) || numericAmount === 0) return "";
  return String(amount);
}

function findBankAccount(bankAccounts, keyword) {
  if (!Array.isArray(bankAccounts) || !keyword) return null;
  const key = String(keyword).toLowerCase().replace("_", " ").trim();
  return (
    bankAccounts.find((b) => {
      const accountType = String(b.accountType || "")
        .toLowerCase()
        .trim();
      const bankName = String(b.bankName || "")
        .toLowerCase()
        .trim();
      return (
        accountType === key ||
        accountType.includes(key) ||
        bankName === key ||
        bankName.includes(key)
      );
    }) || null
  );
}

function nextRowId(rows) {
  const numericIds = rows
    .map((row) => Number(row.id))
    .filter((id) => Number.isFinite(id));
  return numericIds.length ? Math.max(...numericIds) + 1 : 1;
}

function findRowByPayMode(rows, payMode) {
  return rows.find((row) => row.pay_mode === payMode) || null;
}

function emptyRow(payMode, id) {
  return {
    id,
    pay_mode: payMode,
    type: "",
    remarks: "",
    amount: "",
  };
}

function buildDisplayRows(excludedPayModes = [], showCashPaid = true) {
  const excluded = new Set(
    (Array.isArray(excludedPayModes) ? excludedPayModes : []).map(String),
  );
  const rows = PAYMENT_IN_ROW_CONFIG.filter(
    (config) => !excluded.has(config.pay_mode),
  ).map((config) => ({
    ...config,
    isPayable: false,
  }));

  if (showCashPaid) {
    const kasarIndex = rows.findIndex((row) => row.pay_mode === "kasar");
    const insertAt = kasarIndex >= 0 ? kasarIndex : rows.length;
    rows.splice(insertAt, 0, CASH_PAID_ROW_CONFIG);
  }

  return rows;
}

const PaymentSection = ({
  paymentRows,
  onPaymentRowsChange,
  paymentTypes = [],
  bankAccounts = [],
  onOpenUrdFromPaymentRow,
  excludedPayModes = [],
  showCashPaid = true,

  payableRows,
  onPayableRowsChange,

  note,
  onNoteChange,

  showDeliverySection = false,
  applyDeliveryToday,
  onApplyDeliveryTodayChange,
  deliveryProducts = [],
  perItemDeliveryToday = {},
  onPerItemDeliveryTodayChange,

  finalAmount,
  showDiscount = true,
  showGst = true,
  discountType,
  onDiscountTypeChange,
  discountValue,
  onDiscountValueChange,
  discountAmount,
  gstPercentage = "",
  onGstPercentageChange = () => {},
  gstAmount = "0.00",
  roundOffAmount,
  onRoundOffChange,
  grandTotal,
  totalPaid,
  payableAmount,
  remainingAmount,
  outstandingAmount,
  showOutstanding = false,
  followUpDate,
  onFollowUpDateChange,

  buttonClass = "",
  cardClass = "",
  buttonTableStyle = "rounded",
  finalAmountLabel = "Final Amt:",
  grandTotalLabel = "Total",
}) => {
  const showPayableSummary = parseFloat(payableAmount) > 0;
  const showFollowUp = parseFloat(remainingAmount) > 0;
  const roundOffNumeric = parseFloat(roundOffAmount) || 0;
  const roundOffRowLabel = (() => {
    const label = formatRoundOffLabel(roundOffNumeric);
    if (label === "ROUND UP") return "Round Up";
    if (label === "ROUND DOWN") return "Round Down";
    return "Round Off";
  })();
  const saleBalanceNum =
    outstandingAmount !== undefined &&
    outstandingAmount !== null &&
    outstandingAmount !== ""
      ? parseFloat(outstandingAmount) || 0
      : 0;
  const remainingNum = parseFloat(remainingAmount) || 0;
  const outstandingTotal = (saleBalanceNum + remainingNum).toFixed(2);
  const outstandingBreakdown = `${saleBalanceNum.toFixed(2)} + ${remainingNum.toFixed(2)} = ${outstandingTotal}`;

  useEffect(() => {
    const styleId = "payment-summary-row-styles-v10";
    if (document.getElementById(styleId)) return;

    const styleSheet = document.createElement("style");
    styleSheet.id = styleId;
    styleSheet.type = "text/css";
    styleSheet.innerText = PAYMENT_SUMMARY_ROW_CSS;
    document.head.appendChild(styleSheet);
  }, []);

  // Keep cash / cash-paid rows locked to the cash ledger.
  useEffect(() => {
    const cashAccount = findBankAccount(bankAccounts, "cash");
    if (!cashAccount?.id) return;

    const applyCashType = (rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return null;
      const typeId = String(cashAccount.id);
      let changed = false;
      const next = rows.map((row) => {
        if (row.pay_mode !== "cash") return row;
        if (String(row.type) === typeId) return row;
        changed = true;
        return { ...row, type: typeId };
      });
      return changed ? next : null;
    };

    onPaymentRowsChange((prev) => applyCashType(prev) ?? prev);
    onPayableRowsChange((prev) => applyCashType(prev) ?? prev);
  }, [
    bankAccounts,
    paymentRows,
    payableRows,
    onPaymentRowsChange,
    onPayableRowsChange,
  ]);

  const displayRows = useMemo(
    () => buildDisplayRows(excludedPayModes, showCashPaid),
    [excludedPayModes, showCashPaid],
  );
  const finalAmountInWords = useMemo(
    () => formatAmountInWordsParenthetical(finalAmount),
    [finalAmount],
  );

  const upsertRowField = useCallback(
    (isPayable, payMode, field, value) => {
      const onRowsChange = isPayable
        ? onPayableRowsChange
        : onPaymentRowsChange;
      const cashAccount =
        payMode === "cash" ? findBankAccount(bankAccounts, "cash") : null;

      onRowsChange((prevRows) => {
        const idx = prevRows.findIndex((row) => row.pay_mode === payMode);

        if (idx >= 0) {
          return prevRows.map((row, i) => {
            if (i !== idx) return row;
            const next = { ...row, [field]: value };
            if (cashAccount) next.type = cashAccount.id;
            // Kasar never uses a bank account.
            if (payMode === "kasar") next.type = "";
            return next;
          });
        }

        const account = cashAccount || findBankAccount(bankAccounts, payMode);
        const shouldAutoType =
          (!isPayable && ["cash", "wallet"].includes(payMode)) ||
          (isPayable &&
            ["cash", "cheque", "bank_transfer", "wallet"].includes(payMode));

        const newRow = {
          ...emptyRow(payMode, nextRowId(prevRows)),
          [field]: value,
        };
        // Only auto-fill type when the user is not explicitly picking a bank.
        // Otherwise Card/UPI first-select gets wiped (row didn't exist yet).
        if (field !== "type" && shouldAutoType && account) {
          newRow.type = account.id;
        }
        if (cashAccount) newRow.type = cashAccount.id;
        if (payMode === "kasar") newRow.type = "";

        return [...prevRows, newRow];
      });
    },
    [bankAccounts, onPayableRowsChange, onPaymentRowsChange],
  );

  const ensurePaymentRow = useCallback(
    (payMode) => {
      const existing = findRowByPayMode(paymentRows, payMode);
      if (existing) return existing;

      const account =
        payMode === "kasar"
          ? null
          : ["cash", "wallet"].includes(payMode)
            ? findBankAccount(bankAccounts, payMode)
            : null;

      const newRow = {
        ...emptyRow(payMode, nextRowId(paymentRows)),
        type: account?.id || "",
      };

      onPaymentRowsChange((prev) => [...prev, newRow]);
      return newRow;
    },
    [bankAccounts, onPaymentRowsChange, paymentRows],
  );

  const handleOpenUrd = useCallback(
    (payMode) => {
      const existing = findRowByPayMode(paymentRows, payMode);
      if (existing) {
        onOpenUrdFromPaymentRow?.(existing);
        return;
      }

      const newRow = emptyRow(payMode, nextRowId(paymentRows));
      onOpenUrdFromPaymentRow?.(newRow);
    },
    [onOpenUrdFromPaymentRow, paymentRows],
  );

  const handleNoteChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value.length <= 70) {
        onNoteChange(value);
      }
    },
    [onNoteChange],
  );

  const renderTypeCell = (row, config) => {
    const { pay_mode: payMode, isPayable } = config;
    const onChange = (field, value) =>
      upsertRowField(isPayable, payMode, field, value);

    if (!isPayable && isUrdLinkedPayMode(payMode)) {
      const isUrdAttached = Boolean(row?.urd_document_id || row?.urd_data);
      const label = row?.urd_document_id
        ? formatUrdPaymentDisplayLabel(row)
        : row?.urd_data
        ? "URD Attached"
        : "Link URD";

      return (
        <div
          className="d-flex align-items-center px-2"
          style={{ fontSize: "0.85rem", minHeight: PAYMENT_ROW_HEIGHT }}
        >
          <span className="text-truncate" title={label}>
            {label}
          </span>
          {!isUrdAttached ? (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-1"
              style={{ fontSize: "0.75rem" }}
              onClick={() => handleOpenUrd(payMode)}
            >
              Add
            </button>
          ) : row?.urd_data ? (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-1"
              style={{ fontSize: "0.75rem" }}
              onClick={() => handleOpenUrd(payMode)}
            >
              Edit
            </button>
          ) : null}
        </div>
      );
    }

    // Cash type is fixed — always Cash ledger, not a bank dropdown.
    if (payMode === "cash") {
      const cashOption = getCashTypeOption(paymentTypes, bankAccounts);
      return (
        <div
          className="d-flex align-items-center px-2 bg-light"
          style={{
            fontSize: "0.85rem",
            minHeight: PAYMENT_ROW_HEIGHT,
            height: "100%",
            width: "100%",
          }}
          title={cashOption.label}
        >
          <span className="text-truncate">{cashOption.label}</span>
        </div>
      );
    }

    // Kasar has no bank selection — Type is not shown in the UI.
    if (payMode === "kasar") {
      return (
        <div
          className="d-flex align-items-center px-2 bg-light text-muted"
          style={{
            fontSize: "0.85rem",
            minHeight: PAYMENT_ROW_HEIGHT,
            height: "100%",
            width: "100%",
          }}
        >
          —
        </div>
      );
    }

    const typeOptions = getTypeOptionsForPayMode(
      payMode,
      paymentTypes,
      bankAccounts,
      isPayable,
      row,
    );
    const selectedOption = getSelectedTypeOption(row, typeOptions);

    return (
      <Select
        classNamePrefix="payment-type-select"
        styles={customSelectStyles}
        value={selectedOption || null}
        onChange={(selected) =>
          onChange("type", selected ? selected.value : "")
        }
        options={typeOptions}
        placeholder="Select Bank"
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    );
  };

  const renderAmountCell = (row, config) => {
    const { pay_mode: payMode, isPayable } = config;
    const onChange = (field, value) =>
      upsertRowField(isPayable, payMode, field, value);

    const isUrdPayment = !isPayable && isUrdLinkedPayMode(payMode);
    const isReadOnly = isUrdPayment && (Boolean(row?.urd_document_id) || Boolean(row?.urd_data));

    return (
      <input
        type="number"
        className="form-control border-0 rounded-0"
        style={{
          fontSize: "0.85rem",
          padding: "0.35rem 0.5rem",
          height: "100%",
          width: "100%",
          borderRadius: 0,
          boxShadow: "none",
          backgroundColor: isReadOnly ? "#f8f9fa" : "transparent",
          cursor: isReadOnly ? "not-allowed" : "text",
        }}
        placeholder="0.00"
        value={amountInputDisplayValue(row?.amount)}
        onChange={(e) => !isReadOnly && onChange("amount", e.target.value)}
        onFocus={() => {
          if (isUrdPayment && !row?.urd_document_id && !row?.urd_data) {
            handleOpenUrd(payMode);
          } else if (!row) {
            ensurePaymentRow(payMode);
          }
        }}
        readOnly={!!isReadOnly}
      />
    );
  };

  return (
    <div className="mt-2 pt-1 payment-section-root">
      <div className="row g-2">
        <div className="col-12 col-lg-6">
          <div
            className="mb-2 payment-final-amount-line"
            style={{ fontSize: "0.85rem", lineHeight: 1.4 }}
          >
            <span className="payment-final-amount-label">{finalAmountLabel}</span>{" "}
            {finalAmount}{" "}
            <span className="payment-final-amount-words">
              ({finalAmountInWords})
            </span>
          </div>
          <div
            className={`table-responsive border ${
              buttonTableStyle === "square" ? "rounded-0" : "rounded"
            } ${cardClass}`}
          >
            <table className="table table-bordered table-sm m-0">
              <colgroup>
                <col style={{ width: PAYMENT_SUMMARY_LABEL_COL_WIDTH }} />
                <col style={{ width: PAYMENT_SUMMARY_MIDDLE_COL_WIDTH }} />
                <col style={{ width: PAYMENT_SUMMARY_AMT_LABEL_COL_WIDTH }} />
                <col style={{ width: PAYMENT_SUMMARY_AMT_COL_WIDTH }} />
              </colgroup>
              <tbody>
                {showDiscount && (
                  <tr>
                    <td
                      className="p-0 align-middle fw-semibold ps-2"
                      style={{
                        ...PAYMENT_TABLE_TEXT_STYLE,
                        height: PAYMENT_ROW_HEIGHT,
                      }}
                    >
                      Discount
                    </td>
                    <td
                      className="p-0 align-middle"
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <div className="d-flex h-100">
                        <select
                          className={`form-select form-select-sm border-0 rounded-0`}
                          style={{
                            width: "56px",
                            minWidth: "56px",
                            flexShrink: 0,
                            paddingLeft: "6px",
                            paddingRight: "4px",
                            ...PAYMENT_TABLE_TEXT_STYLE,
                          }}
                          value={discountType}
                          onChange={(e) => onDiscountTypeChange(e.target.value)}
                        >
                          <option value="₹">₹</option>
                          <option value="%">%</option>
                        </select>
                        <input
                          type="number"
                          className={PAYMENT_CELL_INPUT_CLASS}
                          placeholder="0.00"
                          value={discountValue}
                          onChange={(e) =>
                            onDiscountValueChange(e.target.value)
                          }
                          style={PAYMENT_CELL_INPUT_STYLE}
                        />
                      </div>
                    </td>
                    <td
                      className="p-0 align-middle fw-semibold ps-2"
                      style={{
                        ...PAYMENT_TABLE_TEXT_STYLE,
                        height: PAYMENT_ROW_HEIGHT,
                      }}
                    >
                      Discount Amt
                    </td>
                    <td
                      className="p-0 align-middle"
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <input
                        type="text"
                        className={PAYMENT_CELL_READONLY_CLASS}
                        readOnly
                        value={discountAmount}
                        style={PAYMENT_CELL_INPUT_STYLE}
                      />
                    </td>
                  </tr>
                )}
                {showGst && (
                <tr>
                  <td
                    className="p-0 align-middle fw-semibold ps-2"
                    style={{
                      ...PAYMENT_TABLE_TEXT_STYLE,
                      height: PAYMENT_ROW_HEIGHT,
                    }}
                  >
                    GST %
                  </td>
                  <td
                    className="p-0 align-middle"
                    style={{ height: PAYMENT_ROW_HEIGHT }}
                  >
                    <input
                      type="number"
                      className={PAYMENT_CELL_INPUT_CLASS}
                      placeholder="0.00"
                      value={gstPercentage}
                      onChange={(e) => onGstPercentageChange(e.target.value)}
                      style={PAYMENT_CELL_INPUT_STYLE}
                    />
                  </td>
                  <td
                    className="p-0 align-middle fw-semibold ps-2"
                    style={{
                      ...PAYMENT_TABLE_TEXT_STYLE,
                      height: PAYMENT_ROW_HEIGHT,
                    }}
                  >
                    GST Amt
                  </td>
                  <td
                    className="p-0 align-middle"
                    style={{ height: PAYMENT_ROW_HEIGHT }}
                  >
                    <input
                      type="text"
                      className={PAYMENT_CELL_READONLY_CLASS}
                      readOnly
                      value={gstAmount}
                      style={PAYMENT_CELL_INPUT_STYLE}
                    />
                  </td>
                </tr>
                )}
                <tr>
                  <td
                    className="p-0 align-middle fw-semibold ps-2"
                    style={{
                      ...PAYMENT_TABLE_TEXT_STYLE,
                      height: PAYMENT_ROW_HEIGHT,
                    }}
                  >
                    {roundOffRowLabel}
                  </td>
                  <td
                    className="p-0 align-middle"
                    style={{ height: PAYMENT_ROW_HEIGHT }}
                  >
                    <input
                      type="text"
                      className={PAYMENT_CELL_INPUT_CLASS}
                      value={roundOffAmount}
                      onChange={onRoundOffChange}
                      placeholder="0.00"
                      style={PAYMENT_CELL_INPUT_STYLE}
                    />
                  </td>
                  <td
                    className={PAYMENT_SUMMARY_SUCCESS_LABEL_CLASS}
                    style={{
                      ...PAYMENT_TABLE_TEXT_STYLE,
                      height: PAYMENT_ROW_HEIGHT,
                    }}
                  >
                    {grandTotalLabel}
                  </td>
                  <td
                    className={PAYMENT_SUMMARY_SUCCESS_VALUE_CELL_CLASS}
                    style={{ height: PAYMENT_ROW_HEIGHT }}
                  >
                    <input
                      type="text"
                      className={PAYMENT_CELL_SUCCESS_VALUE_CLASS}
                      readOnly
                      value={grandTotal}
                      style={PAYMENT_CELL_SUCCESS_INPUT_STYLE}
                    />
                  </td>
                </tr>
                <tr>
                  {showPayableSummary ? (
                    <>
                      <td
                        className={`${PAYMENT_SUMMARY_PENDING_LABEL_CLASS} payment-summary-pending-cell`}
                        style={{
                          ...PAYMENT_TABLE_TEXT_STYLE,
                          height: PAYMENT_ROW_HEIGHT,
                        }}
                      >
                        Payable
                      </td>
                      <td
                        className={`${PAYMENT_SUMMARY_PENDING_VALUE_CELL_CLASS} payment-summary-pending-cell`}
                        style={{ height: PAYMENT_ROW_HEIGHT }}
                      >
                        <input
                          type="text"
                          className={PAYMENT_CELL_PENDING_VALUE_CLASS}
                          readOnly
                          value={payableAmount}
                          style={PAYMENT_CELL_DANGER_INPUT_STYLE}
                        />
                      </td>
                    </>
                  ) : showOutstanding ? (
                    <>
                      <td
                        className="p-0 align-middle fw-semibold ps-2"
                        style={{
                          ...PAYMENT_TABLE_TEXT_STYLE,
                          height: PAYMENT_ROW_HEIGHT,
                        }}
                      >
                        Remaining Amount
                      </td>
                      <td
                        className="p-0 align-middle"
                        style={{ height: PAYMENT_ROW_HEIGHT }}
                      >
                        <input
                          type="text"
                          className={PAYMENT_CELL_READONLY_CLASS}
                          readOnly
                          value={remainingAmount}
                          style={PAYMENT_CELL_INPUT_STYLE}
                        />
                      </td>
                    </>
                  ) : showFollowUp ? (
                    <>
                      <td
                        className="p-0 align-middle fw-semibold ps-2"
                        style={{
                          ...PAYMENT_TABLE_TEXT_STYLE,
                          height: PAYMENT_ROW_HEIGHT,
                        }}
                      >
                        Follow Up
                      </td>
                      <td
                        className="p-0 align-middle"
                        style={{ height: PAYMENT_ROW_HEIGHT }}
                      >
                        <input
                          type="date"
                          className={PAYMENT_CELL_INPUT_CLASS}
                          value={followUpDate}
                          onChange={(e) => onFollowUpDateChange(e.target.value)}
                          style={PAYMENT_CELL_INPUT_STYLE}
                        />
                      </td>
                    </>
                  ) : (
                    <td className="p-0" colSpan={2} />
                  )}
                  {showOutstanding ? (
                    <>
                      <td
                        className={`${PAYMENT_SUMMARY_PENDING_LABEL_CLASS} payment-summary-pending-cell`}
                        style={{
                          ...PAYMENT_TABLE_TEXT_STYLE,
                          height: PAYMENT_ROW_HEIGHT,
                        }}
                      >
                        Outstanding
                      </td>
                      <td
                        className={`${PAYMENT_SUMMARY_PENDING_VALUE_CELL_CLASS} payment-summary-pending-cell`}
                        style={{ height: PAYMENT_ROW_HEIGHT }}
                      >
                        <input
                          type="text"
                          className={PAYMENT_CELL_PENDING_VALUE_CLASS}
                          readOnly
                          value={outstandingTotal}
                          style={PAYMENT_CELL_DANGER_INPUT_STYLE}
                        />
                      </td>
                    </>
                  ) : (
                    <>
                      <td
                        className={`${PAYMENT_SUMMARY_PENDING_LABEL_CLASS} payment-summary-pending-cell`}
                        style={{
                          ...PAYMENT_TABLE_TEXT_STYLE,
                          height: PAYMENT_ROW_HEIGHT,
                        }}
                      >
                        Remaining Amount
                      </td>
                      <td
                        className={`${PAYMENT_SUMMARY_PENDING_VALUE_CELL_CLASS} payment-summary-pending-cell`}
                        style={{ height: PAYMENT_ROW_HEIGHT }}
                      >
                        <input
                          type="text"
                          className={PAYMENT_CELL_PENDING_VALUE_CLASS}
                          readOnly
                          value={remainingAmount}
                          style={PAYMENT_CELL_DANGER_INPUT_STYLE}
                        />
                      </td>
                    </>
                  )}
                </tr>
                {showOutstanding && showPayableSummary && (
                  <tr>
                    <td
                      className="p-0 align-middle fw-semibold ps-2"
                      style={{
                        ...PAYMENT_TABLE_TEXT_STYLE,
                        height: PAYMENT_ROW_HEIGHT,
                      }}
                    >
                      Remaining Amount
                    </td>
                    <td
                      className="p-0 align-middle"
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <input
                        type="text"
                        className={PAYMENT_CELL_READONLY_CLASS}
                        readOnly
                        value={remainingAmount}
                        style={PAYMENT_CELL_INPUT_STYLE}
                      />
                    </td>
                    <td className="p-0" colSpan={2} />
                  </tr>
                )}
                {!showOutstanding && showPayableSummary && showFollowUp && (
                  <tr>
                    <td
                      className="p-0 align-middle fw-semibold ps-2"
                      style={{
                        ...PAYMENT_TABLE_TEXT_STYLE,
                        height: PAYMENT_ROW_HEIGHT,
                      }}
                    >
                      Follow Up
                    </td>
                    <td
                      className="p-0 align-middle"
                      colSpan={3}
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <input
                        type="date"
                        className={PAYMENT_CELL_INPUT_CLASS}
                        value={followUpDate}
                        onChange={(e) => onFollowUpDateChange(e.target.value)}
                        style={PAYMENT_CELL_INPUT_STYLE}
                      />
                    </td>
                  </tr>
                )}
                {showOutstanding && showFollowUp && (
                  <tr>
                    <td
                      className="p-0 align-middle fw-semibold ps-2"
                      style={{
                        ...PAYMENT_TABLE_TEXT_STYLE,
                        height: PAYMENT_ROW_HEIGHT,
                      }}
                    >
                      Follow Up
                    </td>
                    <td
                      className="p-0 align-middle"
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <input
                        type="date"
                        className={PAYMENT_CELL_INPUT_CLASS}
                        value={followUpDate}
                        onChange={(e) => onFollowUpDateChange(e.target.value)}
                        style={PAYMENT_CELL_INPUT_STYLE}
                      />
                    </td>
                    <td
                      className={`${PAYMENT_SUMMARY_PENDING_VALUE_CELL_CLASS} payment-summary-pending-cell`}
                      colSpan={2}
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <input
                        type="text"
                        className={PAYMENT_CELL_PENDING_VALUE_CLASS}
                        readOnly
                        value={outstandingBreakdown}
                        style={{
                          ...PAYMENT_CELL_DANGER_INPUT_STYLE,
                          textAlign: "center",
                          fontSize: "0.8rem",
                        }}
                        title={outstandingBreakdown}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-2">
            <label
              className="form-label fw-semibold d-block"
              style={SUMMARY_NOTE_LABEL_STYLE}
            >
              Note:
            </label>
            <div
              className="position-relative"
              style={SUMMARY_INPUT_WRAPPER_STYLE}
            >
              <textarea
                className={`form-control form-control-sm ${buttonClass}`}
                value={note}
                onChange={handleNoteChange}
                placeholder="Enter note"
                maxLength={70}
                rows={2}
                style={SUMMARY_NOTE_TEXTAREA_STYLE}
              />
              <small
                className="text-muted position-absolute"
                style={{
                  right: "8px",
                  bottom: "6px",
                  fontSize: "0.75rem",
                  pointerEvents: "none",
                }}
              >
                {String(note ?? "").length}/70
              </small>
            </div>
          </div>

          {showDeliverySection && (
            <div className="mt-2">
              <div className="d-flex align-items-center">
                <input
                  className="form-check-input flex-shrink-0 me-2"
                  type="checkbox"
                  id="deliveryCheckbox"
                  checked={applyDeliveryToday}
                  onChange={(e) => onApplyDeliveryTodayChange(e.target.checked)}
                />
                <label
                  className="form-label mb-0 fw-semibold"
                  htmlFor="deliveryCheckbox"
                  style={{
                    fontSize: SUMMARY_NOTE_LABEL_STYLE.fontSize,
                    fontWeight: SUMMARY_NOTE_LABEL_STYLE.fontWeight,
                    marginBottom: 0,
                  }}
                >
                  Product Delivery
                </label>
              </div>
              {!applyDeliveryToday && deliveryProducts.length > 1 && (
                <div className="mt-2 d-flex flex-wrap gap-3">
                  {deliveryProducts.map(({ key, label }) => (
                    <div key={key} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`delivery-${key}`}
                        checked={!!perItemDeliveryToday[key]}
                        onChange={(e) =>
                          onPerItemDeliveryTodayChange((prev) => ({
                            ...prev,
                            [key]: e.target.checked,
                          }))
                        }
                      />
                      <label
                        className="form-check-label ms-2"
                        htmlFor={`delivery-${key}`}
                      >
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="col-12 col-lg-6">
          <div
            className={`table-responsive border ${
              buttonTableStyle === "square" ? "rounded-0" : "rounded"
            } ${cardClass}`}
          >
            <table className="table table-bordered table-sm m-0">
              <colgroup>
                <col style={{ width: PAYMENT_MODE_COL_WIDTH }} />
                <col />
                <col />
                <col style={{ width: PAYMENT_AMT_COL_WIDTH }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="p-0 text-center align-middle payment-modes-header-cell">
                    <div
                      className="d-flex justify-content-center align-items-center px-2"
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <span
                        className="fw-semibold"
                        style={PAYMENT_TABLE_TEXT_STYLE}
                      >
                        Mode
                      </span>
                    </div>
                  </th>
                  <th className="p-0 text-center align-middle payment-modes-header-cell">
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <span
                        className="fw-semibold"
                        style={PAYMENT_TABLE_TEXT_STYLE}
                      >
                        Type
                      </span>
                    </div>
                  </th>
                  <th className="p-0 text-center align-middle payment-modes-header-cell">
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <span
                        className="fw-semibold"
                        style={PAYMENT_TABLE_TEXT_STYLE}
                      >
                        Remarks
                      </span>
                    </div>
                  </th>
                  <th className="p-0 text-center align-middle payment-modes-header-cell">
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: PAYMENT_ROW_HEIGHT }}
                    >
                      <span
                        className="fw-semibold"
                        style={PAYMENT_TABLE_TEXT_STYLE}
                      >
                        Amt
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((config) => {
                  const sourceRows = config.isPayable
                    ? payableRows
                    : paymentRows;
                  const row = findRowByPayMode(sourceRows, config.pay_mode);
                  const rowKey = `${config.isPayable ? "payable" : "payment"}-${config.pay_mode}`;

                  return (
                    <tr key={rowKey}>
                      <td
                        className="p-0 align-middle text-start fw-semibold ps-2 payment-modes-label-cell"
                        style={{
                          ...PAYMENT_TABLE_TEXT_STYLE,
                          height: PAYMENT_ROW_HEIGHT,
                        }}
                      >
                        {config.label}
                      </td>
                      <td
                        className="p-0 align-middle"
                        style={{ height: PAYMENT_ROW_HEIGHT }}
                      >
                        {renderTypeCell(row, config)}
                      </td>
                      <td
                        className="p-0 align-middle"
                        style={{ height: PAYMENT_ROW_HEIGHT }}
                      >
                        <input
                          type="text"
                          className="form-control border-0 rounded-0"
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.35rem 0.5rem",
                            height: "100%",
                            width: "100%",
                            borderRadius: 0,
                            boxShadow: "none",
                          }}
                          value={row?.remarks ?? ""}
                          onChange={(e) =>
                            upsertRowField(
                              config.isPayable,
                              config.pay_mode,
                              "remarks",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td
                        className="p-0 align-middle"
                        style={{ height: PAYMENT_ROW_HEIGHT }}
                      >
                        {renderAmountCell(row, config)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSection;
export {
  PAYMENT_METHOD_LABELS,
  AVAILABLE_PAYMENT_METHODS,
  PAYMENT_MODES_HEADER_BG,
};
